/**
 * Generate a 1080×1080 PNG "I got help today" share card.
 * No message content is included — only a soft inspirational line + brand.
 */

const INSPIRATIONAL_LINES = [
  "Today, I made room for myself.",
  "Showing up counts.",
  "I gave myself a little grace today.",
  "Some days, trying is the win.",
  "Talked it out. Felt seen.",
  "Brought my whole self today.",
  "Took a breath. Took my time.",
  "Reached out instead of pushing through.",
  "Rest is the win today.",
  "I am allowed to be human.",
];

const BG = '#0A0A0A';
const PURPLE = '#8B5CF6';
const GOLD = '#D4AF37';
const TEXT = '#F9FAFB';
const MUTED = '#A1A1AA';

const wrapText = (ctx, text, maxWidth) => {
  const words = text.split(' ');
  const lines = [];
  let current = '';
  for (const w of words) {
    const test = current ? current + ' ' + w : w;
    if (ctx.measureText(test).width <= maxWidth) {
      current = test;
    } else {
      if (current) lines.push(current);
      current = w;
    }
  }
  if (current) lines.push(current);
  return lines;
};

const drawHeart = (ctx, cx, cy, size, color) => {
  ctx.save();
  ctx.translate(cx, cy);
  ctx.scale(size / 100, size / 100);
  ctx.fillStyle = color;
  ctx.beginPath();
  ctx.moveTo(0, 30);
  ctx.bezierCurveTo(-40, -20, -80, 10, 0, 60);
  ctx.bezierCurveTo(80, 10, 40, -20, 0, 30);
  ctx.closePath();
  ctx.fill();
  ctx.restore();
};

export const pickLine = () =>
  INSPIRATIONAL_LINES[Math.floor(Math.random() * INSPIRATIONAL_LINES.length)];

export const drawShareCanvas = async (canvas, line) => {
  // Ensure Outfit font is loaded before drawing
  if (document.fonts?.load) {
    try {
      await Promise.all([
        document.fonts.load('600 96px Outfit'),
        document.fonts.load('500 32px Outfit'),
        document.fonts.load('600 28px Outfit'),
      ]);
      await document.fonts.ready;
    } catch {
      // proceed with fallback font
    }
  }

  const size = 1080;
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');

  // Background
  ctx.fillStyle = BG;
  ctx.fillRect(0, 0, size, size);

  // Purple glow (top-right)
  let grad = ctx.createRadialGradient(size * 0.85, size * 0.15, 0, size * 0.85, size * 0.15, size * 0.7);
  grad.addColorStop(0, 'rgba(139, 92, 246, 0.35)');
  grad.addColorStop(1, 'rgba(139, 92, 246, 0)');
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, size, size);

  // Gold glow (bottom-left)
  grad = ctx.createRadialGradient(size * 0.12, size * 0.88, 0, size * 0.12, size * 0.88, size * 0.7);
  grad.addColorStop(0, 'rgba(212, 175, 55, 0.25)');
  grad.addColorStop(1, 'rgba(212, 175, 55, 0)');
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, size, size);

  // Top label
  ctx.fillStyle = GOLD;
  ctx.font = '600 28px "Outfit", system-ui, sans-serif';
  ctx.textAlign = 'center';
  ctx.letterSpacing = '6px';
  ctx.fillText('I  M A Y  H E L P  Y O U', size / 2, 130);

  // Heart
  drawHeart(ctx, size / 2, 270, 90, GOLD);

  // Main line (wrapped)
  ctx.fillStyle = TEXT;
  ctx.font = '600 96px "Outfit", system-ui, sans-serif';
  ctx.textAlign = 'center';
  const maxWidth = size - 200;
  const lines = wrapText(ctx, line, maxWidth);
  const lineHeight = 120;
  const startY = size / 2 - ((lines.length - 1) * lineHeight) / 2 + 40;
  lines.forEach((ln, i) => ctx.fillText(ln, size / 2, startY + i * lineHeight));

  // Subline
  ctx.fillStyle = MUTED;
  ctx.font = '500 32px "Outfit", system-ui, sans-serif';
  ctx.fillText("It's okay to not be okay today.", size / 2, size - 200);

  // Domain
  ctx.fillStyle = PURPLE;
  ctx.font = '600 36px "Outfit", system-ui, sans-serif';
  ctx.fillText('imayhelpyou.com', size / 2, size - 120);

  return canvas;
};

export const canvasToBlob = (canvas) =>
  new Promise((resolve) => canvas.toBlob(resolve, 'image/png', 0.95));

export const downloadCanvas = async (canvas, filename = 'i-may-help-you.png') => {
  const blob = await canvasToBlob(canvas);
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

export const shareCanvas = async (canvas, line) => {
  const blob = await canvasToBlob(canvas);
  const file = new File([blob], 'i-may-help-you.png', { type: 'image/png' });
  const shareData = {
    title: 'I May Help You',
    text: `${line}\n\nimayhelpyou.com`,
    files: [file],
  };
  if (navigator.canShare && navigator.canShare(shareData)) {
    await navigator.share(shareData);
    return { ok: true, method: 'share' };
  }
  await downloadCanvas(canvas);
  return { ok: true, method: 'download' };
};
